import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import {
  FaRegLightbulb,
  FaExclamationTriangle,
  FaSyncAlt,
  FaBrain,
  FaCalendarAlt,
  FaSearch,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechInput,
  TechButtonGhost,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  StatusPill,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { listInsights } from '../../services/researchService';

const OutputCard = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $color }) => $color || accent};
  border-radius: 8px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }

  &:last-child { margin-bottom: 0; }

  .head {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0.6rem;
    padding-bottom: 0.45rem;
    border-bottom: 1px solid ${borderSoft};

    .label {
      font-family: ${mono};
      font-size: 0.7rem;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: ${({ $color }) => $color || accent};
    }
    .ts {
      margin-left: auto;
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
  }

  .body {
    color: var(--text-secondary);
    font-size: 0.92rem;
    line-height: 1.55;

    h2, h3, h4 { color: var(--text-primary); margin: 0.85em 0 0.35em; font-weight: 600; }
    h3 { font-size: 0.95em; text-transform: uppercase; letter-spacing: 0.1em; color: ${accent}; }
    p { margin: 0 0 0.65em; }
    ul, ol { margin: 0 0 0.7em 1.2em; padding: 0; }
    li { margin-bottom: 0.25em; }
    strong { color: var(--text-primary); }
    code {
      font-family: ${mono};
      font-size: 0.88em;
      padding: 1px 5px;
      background: rgba(0, 0, 0, 0.4);
      border-radius: 3px;
      color: #cfe9f4;
    }
    blockquote {
      border-left: 2px solid ${accent};
      padding-left: 0.85em;
      margin: 0.6em 0;
      color: var(--text-disabled);
    }
  }
`;

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TABS = [
  { id: 'all', label: 'Todos' },
  { id: 'opportunities', label: 'Oportunidades' },
  { id: 'risks', label: 'Riesgos' },
];

// Extrae el contenido bajo un heading `## <header>` hasta el siguiente `##`
// (o el final del documento). Implementación line-based porque el regex
// multiline con anclas no era confiable cuando el cuerpo cierra sin newline.
function extractSection(md, header) {
  if (!md || !header) return null;
  const lines = md.split('\n');
  const target = header.trim().toLowerCase();
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^\s*##\s+(.+?)\s*$/);
    if (m && m[1].toLowerCase() === target) {
      start = i + 1;
      break;
    }
  }
  if (start === -1) return null;
  let end = lines.length;
  for (let i = start; i < lines.length; i++) {
    if (/^\s*##\s+/.test(lines[i])) {
      end = i;
      break;
    }
  }
  const body = lines.slice(start, end).join('\n').trim();
  return body || null;
}

const OpportunitiesRisks = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ insights: [], schedule: null });
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  const load = () => {
    setLoadState({ loading: true, error: null });
    listInsights()
      .then((d) => {
        setData(d || { insights: [], schedule: null });
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => setLoadState({ loading: false, error: e?.message || 'Error' }));
  };

  useEffect(load, []);

  // Cada insight produce dos "vistas": Oportunidades y Riesgos
  // (sus subsecciones markdown).
  const cards = useMemo(() => {
    const out = [];
    for (const ins of data.insights || []) {
      const oppBody =
        extractSection(ins.body, 'Oportunidades detectadas') ||
        extractSection(ins.body, 'Brechas detectadas') ||
        extractSection(ins.body, 'Insight principal');
      const riskBody =
        extractSection(ins.body, 'Riesgos a vigilar') ||
        extractSection(ins.body, 'Brechas comunicacionales') ||
        extractSection(ins.body, 'Solapamientos');

      if (oppBody) {
        out.push({
          id: `opp-${ins.id}`,
          kind: 'opportunity',
          generated_at: ins.generated_at,
          body: oppBody,
          source_kind: ins.kind,
        });
      }
      if (riskBody) {
        out.push({
          id: `risk-${ins.id}`,
          kind: 'risk',
          generated_at: ins.generated_at,
          body: riskBody,
          source_kind: ins.kind,
        });
      }
    }
    return out;
  }, [data.insights]);

  const filtered = useMemo(() => {
    return cards
      .filter((c) => activeTab === 'all' || c.kind === (activeTab === 'opportunities' ? 'opportunity' : 'risk'))
      .filter((c) => !search || c.body.toLowerCase().includes(search.toLowerCase()));
  }, [cards, activeTab, search]);

  const oppCount = cards.filter((c) => c.kind === 'opportunity').length;
  const riskCount = cards.filter((c) => c.kind === 'risk').length;

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader title="Oportunidades y Riesgos" dossier="OPS://OPP-RISK/01" />
        <Loading>Cargando insights reales</Loading>
      </TechShell>
    );
  }
  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Oportunidades y Riesgos" dossier="OPS://OPP-RISK/01" />
        <ErrorBox>No se pudo cargar · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Oportunidades y Riesgos"
        subtitle="Detección automática a partir del monitoreo y el análisis con IA semanal"
        dossier="OPS://OPP-RISK/01"
        actions={<TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>}
      >
        <StatusPill>Oportunidades <strong>{oppCount}</strong></StatusPill>
        <StatusPill>Riesgos <strong>{riskCount}</strong></StatusPill>
        <StatusPill>Próxima auto-generación <strong>{fmtDate(data.schedule?.next_run_at)}</strong></StatusPill>
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaRegLightbulb /> Oportunidades</StatLabel>
          <StatValue>{oppCount}</StatValue>
          <StatSub>extraídas de insights IA</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaExclamationTriangle /> Riesgos</StatLabel>
          <StatValue>{riskCount}</StatValue>
          <StatSub>brechas + amenazas</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaBrain /> Insights</StatLabel>
          <StatValue>{data.insights?.length || 0}</StatValue>
          <StatSub>generados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCalendarAlt /> Cadencia</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>{data.schedule?.cadence || 'weekly'}</StatValue>
          <StatSub>cron domingo 06:00 UTC</StatSub>
        </StatTile>
      </StatStrip>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <FaSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)', fontSize: '0.85rem' }} />
          <TechInput
            placeholder="Buscar oportunidad o riesgo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: '2rem' }}
          />
        </div>
      </div>

      <TechTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      <TechSection title={`${filtered.length} ítems detectados`}>
        {filtered.length === 0 ? (
          <Empty>
            {data.insights?.length === 0
              ? 'Aún no se generaron insights. La primera corrida del cron ocurre el próximo domingo 06:00 UTC.'
              : 'Sin coincidencias para los filtros activos.'}
          </Empty>
        ) : (
          filtered.map((c) => (
            <OutputCard key={c.id} $color={c.kind === 'opportunity' ? 'var(--alert-success)' : 'var(--alert-error)'}>
              <div className="head">
                {c.kind === 'opportunity'
                  ? <FaRegLightbulb style={{ color: 'var(--alert-success)' }} />
                  : <FaExclamationTriangle style={{ color: 'var(--alert-error)' }} />}
                <span className="label">
                  {c.kind === 'opportunity' ? 'Oportunidad' : 'Riesgo'} · {c.source_kind === 'gaps' ? 'brechas' : 'síntesis'}
                </span>
                <span className="ts">generado · {fmtRel(c.generated_at)}</span>
              </div>
              <div className="body">
                <ReactMarkdown>{c.body}</ReactMarkdown>
              </div>
            </OutputCard>
          ))
        )}
      </TechSection>

      <p style={{
        marginTop: '1rem',
        fontFamily: mono,
        fontSize: '0.62rem',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--text-disabled)',
        textAlign: 'center',
      }}>
        Fuente: hallazgos de la investigación con IA, actualizados una vez por semana
      </p>
    </TechShell>
  );
};

export default OpportunitiesRisks;
